import { useState, useCallback, useEffect } from "react";
import type { WeldTypeId } from "@/components/pipe/fdi-data";

export interface WeldEntry {
  id: string;
  qty: number;
  nps: number;
  wall: string;
  materialIdx: number;
  weldTypeId: WeldTypeId;
  branchNps: number;
  headerWall: string;
  linearInches: string;
}

export interface SavedWeldSet {
  id: string;
  name: string;
  savedAt: string;
  entries: WeldEntry[];
}

const STORAGE_KEY = "fdi-saved-weld-sets";

const readSets = (): SavedWeldSet[] => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
};

export const useSavedWeldSets = () => {
  const [sets, setSets] = useState<SavedWeldSet[]>(readSets);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sets));
  }, [sets]);

  const saveSet = useCallback((name: string, entries: WeldEntry[]) => {
    const newSet: SavedWeldSet = {
      id: crypto.randomUUID(),
      name,
      savedAt: new Date().toISOString(),
      entries: entries.map(e => ({ ...e })),
    };
    setSets(prev => [newSet, ...prev]);
    return newSet;
  }, []);

  const deleteSet = useCallback((id: string) => {
    setSets(prev => prev.filter(s => s.id !== id));
  }, []);

  const updateSet = useCallback((id: string, entries: WeldEntry[]) => {
    setSets(prev => prev.map(s => s.id === id ? { ...s, entries: entries.map(e => ({ ...e })), savedAt: new Date().toISOString() } : s));
  }, []);

  return { sets, saveSet, deleteSet, updateSet };
};
